---
title: Settings
lede: How the whole app behaves, and the defaults every workspace starts from.
order: 48
part: Teams and settings
---

Application settings decide how Airlock behaves on this computer and what each workspace inherits. Open them with `⌘,`, or **Settings…** in the Airlock menu.

![The settings rail](media/settings-sections.webp)

The rail lists seven pages, then three groups:

| Group | Pages |
|---|---|
| — | **General**, **Sessions**, **Sandbox**, **Network**, **Git & merge**, **Audit**, **Notifications** |
| Agents | **All agents**, **Skills, MCP & plugins**, **Claude Code**, **Codex** |
| Integrations | One row per integration you have started, then **Add an integration** |
| Housekeeping | **Logs**, **License**, **About** |

A mark on an agent or integration row gives its state: green when it works, amber while it is unfinished, red when a credential was refused. A row with no mark is off.

### Saving

Nothing applies until you press **Save** (`⌘S`) in the header. **Revert** drops your edits, and **Done** returns to your work, asking first if anything is unsaved. A value that cannot be saved, such as a session image that does not parse, marks its page on the rail and puts the caret in the field.

Steps on an integration's page are the exception: they are saved as you take them.

### General

![General settings](media/settings-general.webp)

| Setting | What it does | Default |
|---|---|---|
| **Ask before destroying a session** | Confirms before a session is deleted. Unmerged work is lost either way. | On |
| **Keep this computer awake while a session works** | Holds off idle sleep. The screen may still go dark, and closing the lid still sleeps it. | On |
| **Appearance** | **System**, **Light**, **Dark**, **Charcoal** (darker than Dark, near black with ivory text) or **Dracula**. System follows your OS between light and dark. | Dark |
| **Usage statistics** | Counts, per day, the turns, tokens, cost and what landed, per model and effort, for the Statistics page. Never a prompt, a file or a repository name. Kept on this computer, unless your organization collects them. Off keeps the days already counted. | On |
| **Trail of runs** | In an organization's workspaces, sends it a record of each run: agent, model, outcome, files landed, cost. Never a prompt, code or a secret. Runs in your own workspaces are never sent. | Off |
| **Code editor** | Keys, wrapping, guides, formatting and language servers for Files. See [Files](/files#keys-and-preferences). | — |
| **Application data** | **Reveal folder** opens the [data folder](/data-folder). | — |
| **First-launch setup** | **Run again** brings back the checks and sign-in from your first launch. | — |
| **Restore defaults** | **Restore** resets every page of application settings. Workspaces, their overrides and sessions are untouched. | — |

### Sessions

![Session settings](media/settings-sessions.webp)

| Setting | What it does | Default |
|---|---|---|
| **Sessions start on** | The model and effort the new-session form opens with. Every Claude model, Haiku included, takes all five efforts; GPT-5.5 and GPT-5.2 stop at X-High. | Opus · X-High |
| **Source branch** | The branch a session is cut from. A repository without it uses its own branch. | `main` |
| **New sessions are worked as** | **One instruction**, **Agent proposes** (the agent splits the work into steps first) or **A workflow** from your library, picked under **Default workflow**. | One instruction |
| **Accept the agent's plan** | Lets an agent-proposed workflow run at once instead of waiting for your review. | After review |
| **Sessions working at once** | How many agents may work at once, across every workspace. 1–10. | 3 |
| **Sessions open at once** | How many machines may be up at once, across every workspace. 1–30. | 6 |
| **Park an idle machine after** | Minutes before a finished, untouched session's machine is stopped. `0` never parks. 0–1440. | 30 |
| **Keep archived tasks for** | Days a merged task stays in the [archive](/archive). 1–365. | 30 |

The form can still change any of these for one task. See [Defaults and limits](/limits) for how the two ceilings count.

### Sandbox

| Setting | What it does | Default |
|---|---|---|
| **Backend** | **Micro-VM when available (recommended)**, or **Local — no isolation**. It is the default a new session starts from; see [Where a session runs](/placement). | Micro-VM |
| **Session image** | The Dockerfile every micro-VM session starts from. **Restore default** puts back the shipped one. See [The session image](/session-image). | Shipped image |
| **Docker** | **On, inside the VM** runs a Docker daemon in each micro-VM. See [Docker](/docker). | Off |
| **Docker disk** | Per-session storage for images and containers, removed with the session. 5–500 GB. | 20 GB |
| **CPU cores** | Ceiling per micro-VM, at most half this computer's cores. | 6 |
| **Memory** | Ceiling per micro-VM. A VM boots with 3072 MB and grows toward this. At most half this computer's memory. | 12288 MB |
| **Disk** | Disk per micro-VM, at most half the free space. | 10 GB |
| **Largest repository history** | The most git history a micro-VM session copies in per repository. A larger repository is refused, and the session does not start. Raise **Disk** with it. 64–8192 MB. | 512 MB |
| **Images kept** | Warm images (disks with dependencies already installed) kept across all workspaces. 1–20. | 5 |

### Network

What a micro-VM session may reach. A session on this computer has your computer's network. See [Network](/network).

| Setting | What it does | Default |
|---|---|---|
| **Always allowed** | The agents' endpoints and package registries. Not editable. | — |
| **Additional hosts** | Hosts every workspace may reach. Up to 64. | None |
| **When a session reaches a host nobody listed** | **Ask, holding the call**, **Allow automatically** or **Block — allowlist only**. With Ask or Allow, a micro-VM session puts the host to you; allowing it adds it to the workspace and restarts the session on a machine that reaches it. | Ask |
| **Resolvers** | DNS servers, asked in order. IPv4 only, up to 6. Applies to sessions started afterwards. | `1.1.1.1`, `8.8.8.8` |

### Git & merge

| Setting | What it does | Default |
|---|---|---|
| **Credit the agent in the commit** | Adds a `Co-authored-by` trailer naming the agent. You stay the author. | Off |
| **Sync when the agent hands back** | Rebases the session onto its source branch when that branch has moved. A sync that would conflict is abandoned and left for **Sync**. See [Sync](/sync). | On |

**When a session is finished** lets a session land without you once the agent hands back. Each step needs the one above it, except **Then the session**, which needs **Write the commit and merge**. The run stops at the first refusal.

| Setting | What it does | Default |
|---|---|---|
| **Name the branch** | Lands the work on a new branch, `airlock/<title>-<id>`, or the one the session already names. | Off |
| **Write the commit and merge** | Asks the agent for the commit title and description, then merges. | Off |
| **Push** | Pushes the landed branch to its remote. | Off |
| **Open the pull request** | Opens one against the source branch. Needs a GitHub repository. | Off |
| **Then the session** | **Stays open**, **Stopped** (machine stopped, disk kept) or **Closed** (task archived). | Stays open |

### Audit

What a change is checked against while the agent writes it, and what holds its merge.

![Settings › Audit](media/settings-audit.webp) See [The Change Map](/change-map#the-agent-settles-the-findings) and [Rules](/rules).

| Setting | What it does | Default |
|---|---|---|
| **Audit each hand-back** | When the agent hands back, the team's Rules read its change and the Change Map draws it. What they found is shown under the agent's terminal and on the map, where you strike through what to ignore and send the rest to the agent. **High and Medium** names only those under the terminal, **Every finding** names them all, and the map lists every one either way. **Off** leaves both to your **Audit** and **Send**. | High and Medium |
| **Change Map drawn with** | **Automatic** (Sonnet at low effort, or Terra when Codex is the only agent) or **A model**, with its effort. | Automatic |
| **Hold the merge on rules** | Which rules the audit found broken hold the merge: **Enforced MUST**, **Any MUST**, **MUST and SHOULD** or **None**. | Enforced MUST |
| **Hold the merge on findings** | Which Change Map findings left unsettled hold the merge, while the audit is on: **High**, **High and Medium** or **None**. | High |

Each audit is a turn of your agent's subscription, taken only when there is something to settle. A workspace can answer all four for itself, and an organization can set or lock them in the account.

### Notifications

**Interrupt me when a session**: **Finished, and anything stopped** (default), **Only what is stopped**, or **Never**. See [Notifications](/notifications).

### All agents

| Setting | What it does | Default |
|---|---|---|
| **Run autonomously** | In a micro-VM, skips every approval. On this computer, uses the CLI's automatic mode. Off, the agent asks for everything. | On |
| **Agent panel runs in** | Where the [Agent panel](/agent-panel)'s turns run: **Micro-VM**, a machine thrown away after each turn, or **This computer**, in a copy of the repositories, with no isolation. | Micro-VM |
| **Reuse the agent's config from this computer** | **Copy and sanitize it** copies `~/.claude` or `~/.codex` without credentials, or **Start from nothing**. | Copy |
| **Agent version** | **Update to the newest release on start**, or **Use the version the session image was built with**. Micro-VM only. | Newest |
| **Idle fallback** | Seconds without a hook event before a working session shows as waiting for input. 5–600. | 20 |
| **Commit messages**, **Workflow steps**, **Change Map**, **Pull request comments** | The language the agent writes these in: **English** or **Français**. Pull request comments include the reply a push leaves on the threads it addressed. | English |
| **Mask personal data** | Replaces emails, phone numbers, IBANs, card numbers, French NIRs, public IP addresses, people's names and postal addresses with placeholders such as `[EMAIL_3F9A1C]` in what Airlock sends the agent. A value keeps its placeholder for the whole session. It cannot hide what the agent reads for itself, such as the repositories' files. A workspace can turn it on or off for itself. | Off |
| **Names to mask** | Other names or terms to mask, one per line. | None |
| **Let the agent drive Preview** | Lets the agent open, click and screenshot the session's own pages and send saved calls. See [Agent preview](/agent-preview). | On |
| **…including saved logins** | Lets it sign a page in with a vault login. The password never enters the sandbox. | On |
| **Live probes** | Shown while **Let the agent drive Preview** is on. **Off**, **Attach to what runs — Node and the page**, or **Also start Java, Python and Go under a debugger**. | Attach |

**Skills, MCP & plugins** lists what every session's agent is given beyond its CLI: up to 20 MCP servers, 40 skills and 20 plugins. Anything that could not be installed is named on the session's startup timeline.

### Claude Code and Codex

![The Claude Code page](media/settings-claude-code.webp)

Each agent has its own page. **Use Claude Code** or **Use Codex** turns the agent on or off. While it is off, the page shows nothing else. You cannot turn off the last agent, or one a workflow step still uses.

**Sign-in** differs per agent:

- **Claude Code** keeps a subscription token in the OS keychain. **Set up token** (**Re-run** once one is stored) runs `claude setup-token` and opens your browser. A session on this computer uses your own login instead. **Forget** removes it.
- **Codex** reuses your computer's login. Run `codex login`. A ChatGPT login is copied into the micro-VM. An API key is not.

**Settings** is a list on the left and the chosen setting on the right. Search by key or description, and switch between **All** and **Set here**. **Clear all** empties what you set. A setting left unset keeps the value of your config on this computer, or the agent's default.

The status line is set in its row of that list:

- **Claude Code**: drag blocks such as **Model**, **Git branch** or **Context used** onto **Line 1** and **Line 2**. Click a block to add it to line 1; arrow keys move it and Delete removes it. Under **Your own script**, **Use mine** brings in the status line from `~/.claude`, or you can write a Bash script. Sessions have no `jq`.
- **Codex**: turn items on or off and order them with the arrows. **Codex's own** goes back to its default.

### Integrations

Each integration you started has a page: **Bookmarklet**, **GitHub**, **Jira**, **ngrok**, **Trello**. **Add an integration** lists the rest with **Connect** (**Set up** for the bookmarklet), and **Command line**, where **Install** puts the [`airlock` command](/command-line) on your PATH. Jira and Trello share **Check for new work every**: 10 minutes by default, 2–120. See [Integrations](/integrations).

### Logs

| Setting | What it does | Default |
|---|---|---|
| **Retention per log file** | Size past which a log file is truncated. 16–8192 MB, steps of 16. | 256 MB |
| **Lines kept in the logs dock** | Output the dock holds per project. 200–50000, steps of 100. The files keep everything. | 2000 |

### License

![The License page](media/settings-license.webp)

Shows your **Trial** while you are on one, then **Key**, **Tier**, **Key expiry**, **Access until**, **Serial**, **Machines** (**Manage ↗** opens them in your account) and **Last check**. Under **Add a key**, **Sign in** opens your account in the browser, or paste a key into **License key** and press **Store**. **Check** asks the licence service now, and **Forget** removes the key. See [Your licence key](/licence).

In an organization, **Your account** shows who you are signed in as and your seat. **Open the account page ↗** opens it in your browser, and **Join** clones workspaces shared with you. **Encryption** shows whether this computer holds the organization's key and lists the computers waiting for approval.

### About

![The About page](media/settings-about.webp)

The **Version**, **Runtime backend**, **Micro-VM runtime**, **Data folder**, **Platform**, Electron, Node and Chromium versions, and where the **Third-party licences** are. **Integrations** lists the version of each tool Airlock manages. Quote this page when you contact support.
